import { Link } from 'react-router-dom';
import { ExternalLink, Sparkles } from 'lucide-react';
import { resolveImageUrl } from './ImageUpload';
import { trackBrandClicked } from '../lib/analytics';

/**
 * BrandIntegration
 * ----------------
 * Native "Featured In This Story" sponsor block. Sits inside the article body
 * instead of along the rails, so reads like a tipped hat instead of a banner.
 *
 * Each `feature` is { name, role, url, logo?, blurb? } e.g.
 *   { name: "SEEQ", role: "Used during Ozzie's training routine",
 *     url: "https://drinkseeq.com", logo: "https://...png",
 *     blurb: "Juicy. Clear. Protein." }
 *
 * Renders nothing when the list is empty so we never show an empty shell.
 */
const BrandIntegration = ({ features = [] }) => {
  if (!features || features.length === 0) return null;
  return (
    <aside className="my-12 not-prose" data-testid="brand-integration">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-blue-400" />
        <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300">Featured in this story</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {features.map((f, i) => (
          <a
            key={`${f.name}-${i}`}
            href={f.url || '#'}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => trackBrandClicked({ name: f.name, url: f.url }, 'story_inline')}
            className="group flex items-start gap-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 p-5 transition text-white"
            data-testid={`brand-feature-${i}`}
          >
            {f.logo ? (
              <img loading="lazy" decoding="async" src={resolveImageUrl(f.logo, 160)} alt={f.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-white ring-1 ring-white/15" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-white/10 text-white font-display flex items-center justify-center text-lg flex-shrink-0">
                {(f.name || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-display text-lg uppercase leading-none">{f.name}</p>
                <ExternalLink className="w-3.5 h-3.5 text-white/40 group-hover:text-white transition" />
              </div>
              {f.role && <p className="font-italic text-[13px] text-white/60 mt-1.5">{f.role}</p>}
              {f.blurb && <p className="text-sm text-white/50 mt-1">{f.blurb}</p>}
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold uppercase tracking-widest text-blue-400">
                Learn more →
              </span>
            </div>
          </a>
        ))}
      </div>
      <div className="mt-4 text-center">
        <p className="text-xs text-white/50">
          Interested in becoming a featured partner?{' '}
          <Link to="/brands" className="font-bold underline text-blue-300 hover:text-blue-200" data-testid="brand-partner-cta">
            Partner With Dreams League →
          </Link>
        </p>
      </div>
    </aside>
  );
};

export default BrandIntegration;
